import React, { useState } from 'react';
import { ImagePlus, Link, Type, Lock, LockOpen, ChevronUp, Plus, CopyPlus, Trash2 } from 'lucide-react';
import { ImageUploadPanel } from './ImageUploadPanel';
import { SlidePicker } from './SlidePicker';

interface DeckToolbarProps {
  onAddImage?: () => void;
  onImageUpload?: (file: File) => void;
  onSelectFromLibrary?: () => void;
  onSelectEmoji?: () => void;
  onSelectRecentImage?: (imageUrl: string) => void;
  onAddLink?: () => void;
  onAddText?: () => void;
  onToggleLock?: () => void;
  onLockWithUnlockSlide?: (unlockOnSlide: number | null) => void;
  onNewSlide?: () => void;
  onCopySlide?: () => void;
  onDeleteSlide?: () => void;
  isLocked?: boolean;
  lockPosition?: 'above' | 'below';
  unlockOnSlide?: number;
  currentSlideIndex?: number;
  totalSlides?: number;
  onLockPositionChange?: (position: 'above' | 'below') => void;
  onUnlockSlideChange?: (slideIndex: number | null) => void;
  recentImages?: string[];
}

export const DeckToolbar: React.FC<DeckToolbarProps> = ({
  onImageUpload,
  onSelectFromLibrary,
  onSelectEmoji,
  onSelectRecentImage,
  onAddLink,
  onAddText,
  onToggleLock,
  onLockWithUnlockSlide,
  onNewSlide,
  onCopySlide,
  onDeleteSlide,
  isLocked = false,
  lockPosition,
  unlockOnSlide,
  currentSlideIndex = 0,
  totalSlides = 1,
  onLockPositionChange,
  onUnlockSlideChange,
  recentImages = [],
}) => {
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [showSlidePicker, setShowSlidePicker] = useState(false);

  const handleImageUpload = (file: File) => {
    onImageUpload?.(file);
    setShowImagePanel(false);
  };

  const handleSelectFromLibrary = () => {
    setShowImagePanel(false);
    onSelectFromLibrary?.();
  };

  const handleSelectEmoji = () => {
    onSelectEmoji?.();
    setShowImagePanel(false);
  };

  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
      {/* Image Upload Panel */}
      {showImagePanel && (
        <div className="absolute bottom-[74px] left-0 mb-2">
          <ImageUploadPanel
            onUpload={handleImageUpload}
            onSelectFromLibrary={handleSelectFromLibrary}
            onSelectEmoji={handleSelectEmoji}
            onSelectRecentImage={(url) => {
              onSelectRecentImage?.(url);
              setShowImagePanel(false);
            }}
            onClose={() => setShowImagePanel(false)}
            recentImages={recentImages}
          />
        </div>
      )}

      {/* Slide Picker Panel */}
      {showSlidePicker && (
        <SlidePicker
          currentSlideIndex={currentSlideIndex}
          totalSlides={totalSlides}
          onSelectSlide={(slideIndex) => {
            onUnlockSlideChange?.(slideIndex);
            setShowSlidePicker(false);
          }}
          onClose={() => setShowSlidePicker(false)}
        />
      )}

      <div className="flex flex-row items-center">
        {/* Add Image */}
        <button
          onClick={() => setShowImagePanel(!showImagePanel)}
          className={`w-[58px] h-[58px] flex items-center justify-center border-2 border-[#DFE4EA] backdrop-blur-[12.5px] rounded-l-lg transition-colors ${
            showImagePanel
              ? 'bg-[rgba(208,226,246,0.3)]'
              : 'bg-[rgba(32,40,53,0.9)] hover:bg-[rgba(208,226,246,0.3)]'
          }`}
          title="Add Image"
        >
          <ImagePlus size={30} className="text-[#DFE4EA]" />
        </button>

        {/* Add Link */}
        <button
          onClick={onAddLink}
          className="w-[58px] h-[58px] flex items-center justify-center bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] hover:bg-[rgba(208,226,246,0.3)] transition-colors"
          title="Add Link"
        >
          <Link size={30} className="text-[#DFE4EA]" />
        </button>

        {/* Add Text */}
        <button
          onClick={onAddText}
          className="w-[58px] h-[58px] flex items-center justify-center bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] hover:bg-[rgba(208,226,246,0.3)] transition-colors"
          title="Add Text"
        >
          <Type size={30} className="text-[#DFE4EA]" />
        </button>

        {/* Lock/Unlock Slide */}
        <button
          onClick={() => {
            if (isLocked) {
              onToggleLock?.();
              setShowSlidePicker(false);
            } else {
              onLockWithUnlockSlide?.(null);
            }
          }}
          className={`w-[58px] h-[58px] flex items-center justify-center border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] transition-colors ${
            isLocked
              ? 'bg-[rgba(208,226,246,0.3)]'
              : 'bg-[rgba(32,40,53,0.9)] hover:bg-[rgba(208,226,246,0.3)]'
          }`}
          title={isLocked ? "Locked" : "Lock Slide"}
        >
          {isLocked ? <Lock size={30} className="text-[#DFE4EA]" /> : <LockOpen size={30} className="text-[#DFE4EA]" />}
        </button>

        {/* Unlock Control - shown when locked */}
        {isLocked && (
          <div className="relative flex items-center gap-2 bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] px-4 h-[58px]">
            <button
              onClick={() => setShowSlidePicker(!showSlidePicker)}
              className="flex flex-col items-start hover:opacity-80 transition-opacity"
            >
              <div className="flex items-center gap-1">
                <span className="text-[#DFE4EA] text-sm font-medium">Unlock:</span>
                <ChevronUp size={16} className="text-[#DFE4EA]" />
              </div>
              <span className="text-[#DFE4EA] text-xs">
                {unlockOnSlide !== undefined && unlockOnSlide > currentSlideIndex
                  ? (() => {
                      const relativePosition = unlockOnSlide - currentSlideIndex;
                      if (relativePosition === 1) return 'Next Slide';
                      if (relativePosition === 2) return 'After Next Slide';
                      return `After ${relativePosition - 1} Slides`;
                    })()
                  : 'Never'}
              </span>
            </button>
          </div>
        )}

        {/* Position Control - shown when locked */}
        {isLocked && (
          <div className="flex items-center gap-2 bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] px-4 h-[58px]">
            <button
              onClick={() => {
                const newPosition = lockPosition === 'above' ? 'below' : 'above';
                onLockPositionChange?.(newPosition);
              }}
              className="flex flex-col items-start hover:opacity-80 transition-opacity"
            >
              <div className="flex items-center gap-1">
                <span className="text-[#DFE4EA] text-sm font-medium">Position</span>
                <ChevronUp size={16} className="text-[#DFE4EA]" />
              </div>
              <span className="text-[#DFE4EA] text-xs capitalize">
                {lockPosition || 'Above'}
              </span>
            </button>
          </div>
        )}

        {/* New Slide */}
        <button
          onClick={onNewSlide}
          className="w-[58px] h-[58px] flex items-center justify-center bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] hover:bg-[rgba(208,226,246,0.3)] transition-colors"
          title="New Slide"
        >
          <Plus size={30} className="text-[#DFE4EA]" />
        </button>

        {/* Copy Slide */}
        <button
          onClick={onCopySlide}
          className="w-[58px] h-[58px] flex items-center justify-center bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] hover:bg-[rgba(208,226,246,0.3)] transition-colors"
          title="Copy Slide"
        >
          <CopyPlus size={30} className="text-[#DFE4EA]" />
        </button>

        {/* Delete Slide */}
        <button
          onClick={onDeleteSlide}
          className="w-[58px] h-[58px] flex items-center justify-center bg-[rgba(32,40,53,0.9)] border-2 border-l-0 border-[#DFE4EA] backdrop-blur-[12.5px] rounded-r-lg hover:bg-[rgba(208,226,246,0.3)] transition-colors"
          title="Delete Slide"
        >
          <Trash2 size={30} className="text-[#DFE4EA]" />
        </button>
      </div>
    </div>
  );
};
